import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    SupplierPaymentItem,
    SupplierStatementResponse,
} from '@/api/models';
import {
    useDeleteSupplierPaymentMutation,
    useSupplierPaymentsQuery,
    useSupplierStatementQuery,
} from '@/api/queries';
import { SUPPLIER_PAYMENTS_PAGE_SIZE } from '../suppliersMeta';

export type SupplierDetailTab = 'statement' | 'payments';

type UseSupplierDetailDrawerParams = {
    /** The supplier to show; `null` closes the drawer and disables both reads. */
    supplierId: string | null;
};

/**
 * Data for the supplier detail drawer: the running statement and the payments
 * list, each self-served.
 *
 * The two are separate queries rather than one, because they are separate tabs
 * and the user may never open the second — and because the statement is a whole
 * document while the payments list is paged. Deleting a payment invalidates
 * every supplier query, so the statement, the list and the management table all
 * move together.
 */
export const useSupplierDetailDrawer = ({
    supplierId,
}: UseSupplierDetailDrawerParams) => {
    const [tab, setTab] = React.useState<SupplierDetailTab>('statement');
    const [page, setPage] = React.useState(1);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);

    // A different supplier is a different account: start it on its own first
    // page and first tab rather than inheriting the last one's position.
    React.useEffect(() => {
        setTab('statement');
        setPage(1);
        setDeleteError(null);
    }, [supplierId]);

    const statementQuery = useSupplierStatementQuery(supplierId);
    const paymentsQuery = useSupplierPaymentsQuery(supplierId, {
        page,
        pageSize: SUPPLIER_PAYMENTS_PAGE_SIZE,
    });
    const deleteMutation = useDeleteSupplierPaymentMutation();

    const statement: SupplierStatementResponse | null =
        statementQuery.data && statementQuery.data.success
            ? statementQuery.data.response
            : null;

    const statementError: ErrorResponse | null =
        statementQuery.data && !statementQuery.data.success
            ? statementQuery.data.error
            : null;

    const paymentsResponse =
        paymentsQuery.data && paymentsQuery.data.success
            ? paymentsQuery.data.response
            : null;

    const payments: SupplierPaymentItem[] | null =
        paymentsResponse?.items ?? null;

    const paymentsError: ErrorResponse | null =
        paymentsQuery.data && !paymentsQuery.data.success
            ? paymentsQuery.data.error
            : null;

    const onTabChange = React.useCallback((id: string) => {
        setTab(id as SupplierDetailTab);
    }, []);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onDeletePayment = React.useCallback(
        async (paymentId: string) => {
            if (supplierId === null) {
                return;
            }
            setDeleteError(null);
            const result = await deleteMutation.mutateAsync({
                supplierId,
                paymentId,
            });
            if (!result.success) {
                setDeleteError(result.error.message ?? null);
            }
        },
        [deleteMutation, supplierId],
    );

    const onRetry = React.useCallback(() => {
        statementQuery.refetch();
        paymentsQuery.refetch();
    }, [paymentsQuery, statementQuery]);

    return {
        deleteError,
        isDeleting: deleteMutation.isPending,
        isLoadingPayments: paymentsQuery.isLoading,
        isLoadingStatement: statementQuery.isLoading,
        onDeletePayment,
        onPageChange,
        onRetry,
        onTabChange,
        page,
        payments,
        paymentsError,
        statement,
        statementError,
        tab,
        totalPages: paymentsResponse?.totalPages ?? 1,
    };
};
